"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { handleError } from "@/lib/handle-error";

interface UseCopyToClipboardOptions {
  resetDelayMs?: number;
}

interface UseCopyToClipboardReturn {
  copy: (value: string | Promise<string>, label?: string) => Promise<boolean>;
  copied: boolean;
}

async function writeClipboard(value: string | Promise<string>): Promise<void> {
  if (typeof value === "string") return navigator.clipboard.writeText(value);
  if (typeof ClipboardItem === "undefined") {
    return navigator.clipboard.writeText(await value);
  }
  return navigator.clipboard.write([new ClipboardItem({ "text/plain": value })]);
}

export function useCopyToClipboard({
  resetDelayMs = 1500,
}: UseCopyToClipboardOptions = {}): UseCopyToClipboardReturn {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const copy = useCallback(
    async (value: string | Promise<string>, label = "Copied"): Promise<boolean> => {
      try {
        await writeClipboard(value);
      } catch (error) {
        handleError(error, { display: "banner" });
        return false;
      }

      toast.success(label);
      if (mountedRef.current) {
        setCopied(true);
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
          if (mountedRef.current) setCopied(false);
        }, resetDelayMs);
      }
      return true;
    },
    [resetDelayMs],
  );

  return { copy, copied };
}
